import { useEffect, useMemo, useState } from "react";
import {
  Student,
  StudentListSortDir,
  StudentListSortField,
  StudentStatus,
  bulkSetStudentStatus,
  buildWhatsAppUrl,
  setStudentStatus,
  useStudentList,
} from "../api/students";
import {
  TrainingGroupRef,
  TrainingPeriodTree,
  useTrainingCohortTree,
} from "../api/trainingCohorts";
import { BranchFilterSelect } from "../components/common/BranchFilterSelect";
import {
  DEFAULT_STUDENT_LIST_STATUS_FILTER,
  StudentListStatusFilterSelect,
  type StudentListStatusFilter,
} from "../components/common/StudentListStatusFilterSelect";
import { usePlatformBranchFilter } from "../hooks/usePlatformBranchFilter";
import { useAuth } from "../auth/AuthContext";
import { WhatsAppLink } from "../components/WhatsAppLink";
import { formatPhoneDisplay } from "../utils/phone";
import { StudentFormModal } from "../components/students/StudentFormModal";
import {
  STUDENT_STATUS_LABELS,
  StudentStatusSelect,
} from "../components/students/StudentStatusSelect";
import { StudentLessonProgressCell } from "../components/students/StudentLessonProgress";
import { StudentLessonStatsDrawer } from "../components/students/StudentLessonStatsDrawer";
import StudentImportModal from "../components/students/StudentImportModal";
import ExamEntryPdfImportModal from "../components/students/ExamEntryPdfImportModal";
import { TrainingPeriodModal } from "../components/students/TrainingPeriodModal";
import { TrainingCohortManageModal } from "../components/cohorts/TrainingCohortManageModal";
import { UI_LOADING } from "../constants/uiLabels";
import { ListPaginationBar, useListPageSize, usePagedOffset } from "../components/ListPagination";
import { useRegisterListPageStats } from "../context/ListPageStatsContext";
import "./StudentsPage.css";

function daysUntil(dateIso: string | null): number | null {
  if (!dateIso) return null;
  const target = new Date(dateIso);
  if (Number.isNaN(target.getTime())) return null;
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  return Math.round((target.getTime() - today.getTime()) / (1000 * 60 * 60 * 24));
}

function formatExamDateShort(value: string | null): string {
  if (!value) return "—";
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return value;
  return date.toLocaleDateString("tr-TR", { day: "numeric", month: "short", year: "numeric" });
}

function formatTimeHm(value: string | null | undefined): string | null {
  if (!value) return null;
  const part = value.slice(0, 5);
  return /^\d{2}:\d{2}$/.test(part) ? part : null;
}

function countdownLabel(days: number | null): string | null {
  if (days === null) return null;
  if (days < 0) return "gecmis";
  if (days === 0) return "bugun";
  if (days <= 30) return `${days}g`;
  return null;
}

function CompactExamDateCell({
  date,
  time,
}: {
  date: string | null | undefined;
  time?: string | null;
}) {
  const examIn = daysUntil(date ?? null);
  const tone =
    examIn === null
      ? ""
      : examIn < 0
        ? "kp-students__exam-col--past"
        : examIn <= 7
          ? "kp-students__exam-col--soon"
          : "";
  const timeLabel = formatTimeHm(time ?? null);
  const countdown = countdownLabel(examIn);

  return (
    <td className={`kp-students__exam-col kp-cell-nowrap${tone ? ` ${tone}` : ""}`}>
      <span className="kp-students__exam-inline">
        <span className="kp-students__exam-date">{formatExamDateShort(date ?? null)}</span>
        {timeLabel && <span className="kp-students__exam-meta">{timeLabel}</span>}
        {countdown && <span className="kp-students__exam-meta kp-students__exam-countdown">{countdown}</span>}
      </span>
    </td>
  );
}

function findPeriod(tree: TrainingPeriodTree[], periodId: string): TrainingPeriodTree | undefined {
  return tree.find((p) => p.id === periodId);
}

function findGroup(period: TrainingPeriodTree | undefined, groupId: string): TrainingGroupRef | undefined {
  return period?.groups.find((g) => g.id === groupId);
}

function SortableHeader({
  label,
  column,
  sortBy,
  sortDir,
  onSort,
}: {
  label: string;
  column: StudentListSortField;
  sortBy: StudentListSortField;
  sortDir: StudentListSortDir;
  onSort: (column: StudentListSortField) => void;
}) {
  const active = sortBy === column;
  return (
    <th scope="col">
      <button
        type="button"
        className={`kp-students__sort${active ? " kp-students__sort--active" : ""}`}
        onClick={() => onSort(column)}
        aria-sort={active ? (sortDir === "asc" ? "ascending" : "descending") : "none"}
      >
        {label}
        <span className="kp-students__sort-icon" aria-hidden>
          {active ? (sortDir === "asc" ? "▲" : "▼") : "↕"}
        </span>
      </button>
    </th>
  );
}

export function StudentsPage() {
  const [searchInput, setSearchInput] = useState("");
  const [appliedSearch, setAppliedSearch] = useState("");
  const [statusFilter, setStatusFilter] = useState<StudentListStatusFilter>(
    DEFAULT_STUDENT_LIST_STATUS_FILTER,
  );
  const { isPlatformOwner, branchFilter, setBranchFilter, branches } = usePlatformBranchFilter();
  const [periodFilter, setPeriodFilter] = useState("");
  const [groupFilter, setGroupFilter] = useState("");
  const [subgroupFilter, setSubgroupFilter] = useState("");
  const [cohortUnassigned, setCohortUnassigned] = useState(false);
  const [editing, setEditing] = useState<Student | null>(null);
  const [lessonStatsFor, setLessonStatsFor] = useState<Student | null>(null);
  const [creating, setCreating] = useState(false);
  const [importing, setImporting] = useState(false);
  const [examPdfImporting, setExamPdfImporting] = useState(false);
  const [creatingPeriod, setCreatingPeriod] = useState(false);
  const [managingCohorts, setManagingCohorts] = useState(false);
  const [sortBy, setSortBy] = useState<StudentListSortField>("full_name");
  const [sortDir, setSortDir] = useState<StudentListSortDir>("asc");
  const [selectedIds, setSelectedIds] = useState<Set<string>>(() => new Set());
  const [bulkBusy, setBulkBusy] = useState(false);
  const [statusMessage, setStatusMessage] = useState<string | null>(null);
  const [pageError] = useState<string | null>(null);
  const { session } = useAuth();
  const role = session?.user?.role;
  const canManageStatus = role === "tenant_admin" || role === "manager" || role === "office_staff";
  const canImport = role === "tenant_admin" || role === "manager" || role === "office_staff";
  const canCreatePeriod = role === "tenant_admin" || role === "manager" || role === "office_staff";

  const cohortTree = useTrainingCohortTree();

  useEffect(() => {
    const handle = setTimeout(() => {
      setAppliedSearch(searchInput.trim());
    }, 300);
    return () => clearTimeout(handle);
  }, [searchInput]);

  const selectedPeriod = useMemo(
    () => findPeriod(cohortTree.tree, periodFilter),
    [cohortTree.tree, periodFilter],
  );
  const selectedGroup = useMemo(
    () => findGroup(selectedPeriod, groupFilter),
    [selectedPeriod, groupFilter],
  );

  const handleSort = (column: StudentListSortField) => {
    if (sortBy === column) {
      setSortDir((d) => (d === "asc" ? "desc" : "asc"));
      return;
    }
    setSortBy(column);
    setSortDir("asc");
  };

  const pageSize = useListPageSize();
  const { offset, setOffset } = usePagedOffset(
    JSON.stringify([
      appliedSearch,
      statusFilter,
      branchFilter,
      periodFilter,
      groupFilter,
      subgroupFilter,
      cohortUnassigned,
      sortBy,
      sortDir,
      pageSize,
    ]),
  );

  const list = useStudentList({
    q: appliedSearch || undefined,
    status: statusFilter || undefined,
    branchId: branchFilter || undefined,
    trainingPeriodId: cohortUnassigned ? undefined : periodFilter || undefined,
    trainingGroupId: cohortUnassigned ? undefined : groupFilter || undefined,
    trainingSubgroupId: cohortUnassigned ? undefined : subgroupFilter || undefined,
    cohortUnassigned: cohortUnassigned || undefined,
    sort: sortBy,
    order: sortDir,
    limit: pageSize,
    offset,
  });

  const items = list.data?.items || [];
  const total = list.data?.total || 0;
  const allVisibleSelected = items.length > 0 && selectedIds.size === items.length;

  useRegisterListPageStats({
    label: "kursiyer",
    visible: items.length,
    total,
    loading: list.loading,
  });

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const toggleSelectAll = () => {
    if (allVisibleSelected) {
      setSelectedIds(new Set());
      return;
    }
    setSelectedIds(new Set(items.map((student) => student.id)));
  };

  const handleRowStatus = async (studentId: string, status: StudentStatus) => {
    setStatusMessage(null);
    try {
      await setStudentStatus(studentId, status);
      list.reload();
    } catch (err) {
      setStatusMessage(err instanceof Error ? err.message : "Durum guncellenemedi.");
      throw err;
    }
  };

  const handleBulkStatus = async (status: StudentStatus) => {
    if (selectedIds.size === 0 || bulkBusy) return;
    setBulkBusy(true);
    setStatusMessage(null);
    try {
      const result = await bulkSetStudentStatus([...selectedIds], status);
      setSelectedIds(new Set());
      const skipped =
        result.skipped_count > 0 ? ` (${result.skipped_count} atlandi)` : "";
      setStatusMessage(
        `${result.updated_count} kursiyer ${STUDENT_STATUS_LABELS[status]} yapildi${skipped}.`,
      );
      list.reload();
    } catch (err) {
      setStatusMessage(err instanceof Error ? err.message : "Toplu guncelleme basarisiz.");
    } finally {
      setBulkBusy(false);
    }
  };

  const summary = useMemo(() => {
    return `${total} kayit`;
  }, [total]);

  const cohortFilterSummary = useMemo(() => {
    if (cohortUnassigned) return "Atanmamis adaylar";
    if (subgroupFilter && selectedGroup) {
      const sg = selectedGroup.subgroups.find((s) => s.id === subgroupFilter);
      if (sg && selectedPeriod) {
        return `${selectedPeriod.label} · ${selectedGroup.label} · ${sg.label}`;
      }
    }
    if (groupFilter && selectedPeriod && selectedGroup) {
      return `${selectedPeriod.label} · ${selectedGroup.label}`;
    }
    if (periodFilter && selectedPeriod) return selectedPeriod.label;
    return null;
  }, [
    cohortUnassigned,
    subgroupFilter,
    groupFilter,
    periodFilter,
    selectedPeriod,
    selectedGroup,
  ]);

  return (
    <div className="kp-students">
      <div className="kp-students__header">
        <div>
          <h1>Kursiyerler</h1>
          <p>
            {summary}
            {cohortFilterSummary && (
              <>
                {" "}
                · <strong>{cohortFilterSummary}</strong>
              </>
            )}
          </p>
        </div>
        <div className="kp-students__header-actions">
          {canCreatePeriod && (
            <>
              <button className="kp-btn" type="button" onClick={() => setCreatingPeriod(true)}>
                Yeni Kayit Donemi
              </button>
              <button className="kp-btn" type="button" onClick={() => setManagingCohorts(true)}>
                Donem / Grup Yonetimi
              </button>
            </>
          )}
          {canImport && (
            <>
              <button className="kp-btn" type="button" onClick={() => setImporting(true)}>
                Excel ile Toplu Yukle
              </button>
              <button className="kp-btn" type="button" onClick={() => setExamPdfImporting(true)}>
                Teorik Sınav PDF
              </button>
            </>
          )}
          <button className="kp-btn kp-btn--primary" type="button" onClick={() => setCreating(true)}>
            + Yeni Kursiyer
          </button>
        </div>
      </div>

      <div className="kp-students__toolbar kp-card kp-students__toolbar--search">
        <input
          className="kp-input"
          type="search"
          placeholder="Ad, telefon veya TC ile ara..."
          value={searchInput}
          onChange={(e) => setSearchInput(e.target.value)}
        />
        <StudentListStatusFilterSelect value={statusFilter} onChange={setStatusFilter} />
        {isPlatformOwner && (
          <BranchFilterSelect
            value={branchFilter}
            onChange={setBranchFilter}
            branches={branches}
          />
        )}
        <button className="kp-btn" type="button" onClick={list.reload} disabled={list.loading}>
          Yenile
        </button>
      </div>

      <div className="kp-students__toolbar kp-card kp-students__toolbar--cohort">
        <span className="kp-students__cohort-filter-label">Dönem Grup Filtresi</span>
        <div className="kp-students__cohort-filter-fields">
        <select
          className="kp-input kp-input--compact"
          value={cohortUnassigned ? "__unassigned" : periodFilter}
          disabled={cohortTree.loading}
          onChange={(e) => {
            const v = e.target.value;
            if (v === "__unassigned") {
              setCohortUnassigned(true);
              setPeriodFilter("");
              setGroupFilter("");
              setSubgroupFilter("");
              return;
            }
            setCohortUnassigned(false);
            setPeriodFilter(v);
            setGroupFilter("");
            setSubgroupFilter("");
          }}
        >
          <option value="">Tüm Dönemler</option>
          <option value="__unassigned">Atanmamış</option>
          {cohortTree.tree.map((p) => (
            <option key={p.id} value={p.id}>
              {p.label}
            </option>
          ))}
        </select>
        <select
          className="kp-input kp-input--compact"
          value={groupFilter}
          disabled={cohortUnassigned || !periodFilter}
          onChange={(e) => {
            setGroupFilter(e.target.value);
            setSubgroupFilter("");
          }}
        >
          <option value="">Tüm Gruplar</option>
          {selectedPeriod?.groups.map((g) => (
            <option key={g.id} value={g.id}>
              {g.label}
            </option>
          ))}
        </select>
        <select
          className="kp-input kp-input--compact"
          value={subgroupFilter}
          disabled={cohortUnassigned || !groupFilter}
          onChange={(e) => setSubgroupFilter(e.target.value)}
        >
          <option value="">Tüm Alt Gruplar</option>
          {selectedGroup?.subgroups.map((sg) => (
            <option key={sg.id} value={sg.id}>
              {sg.label}
              {sg.member_count > 0 ? ` (${sg.member_count})` : ""}
            </option>
          ))}
        </select>
        <button
          className="kp-btn kp-btn--ghost kp-btn--compact"
          type="button"
          disabled={!periodFilter && !cohortUnassigned}
          onClick={() => {
            setPeriodFilter("");
            setGroupFilter("");
            setSubgroupFilter("");
            setCohortUnassigned(false);
          }}
        >
          Temizle
        </button>
        </div>
      </div>

      {cohortTree.error && <div className="kp-card kp-error">{cohortTree.error}</div>}
      {pageError && <div className="kp-card kp-error">{pageError}</div>}
      {statusMessage && <div className="kp-card kp-form-status__hint">{statusMessage}</div>}

      <div className="kp-students__table-wrapper kp-card">
        {list.loading && <div className="kp-loading">{UI_LOADING}</div>}
        {list.error && <div className="kp-error">{list.error}</div>}

        {!list.loading && !list.error && items.length === 0 && (
          <div className="kp-empty">Bu kriterlere uyan kursiyer bulunamadi.</div>
        )}

        {!list.loading && !list.error && items.length > 0 && (
          <>
            {canManageStatus && selectedIds.size > 0 && (
              <div className="kp-students__bulk-bar">
                <strong>{selectedIds.size} secili</strong>
                <div className="kp-students__bulk-actions">
                  <button
                    type="button"
                    className="kp-btn"
                    disabled={bulkBusy}
                    onClick={() => void handleBulkStatus("active")}
                  >
                    Aktif yap
                  </button>
                  <button
                    type="button"
                    className="kp-btn"
                    disabled={bulkBusy}
                    onClick={() => void handleBulkStatus("passive")}
                  >
                    Pasif yap
                  </button>
                  <button
                    type="button"
                    className="kp-btn"
                    disabled={bulkBusy}
                    onClick={() => void handleBulkStatus("graduated")}
                  >
                    Mezun yap
                  </button>
                  <button
                    type="button"
                    className="kp-btn kp-btn--ghost"
                    disabled={bulkBusy}
                    onClick={() => setSelectedIds(new Set())}
                  >
                    Secimi temizle
                  </button>
                </div>
              </div>
            )}
          <table className="kp-students__table kp-data-table">
            <thead>
              <tr>
                {canManageStatus && (
                  <th scope="col" className="kp-students__select-col">
                    <input
                      type="checkbox"
                      checked={allVisibleSelected}
                      aria-label="Tumunu sec"
                      onChange={toggleSelectAll}
                    />
                  </th>
                )}
                <th scope="col" className="kp-students__tc-col">
                  TC
                </th>
                <SortableHeader
                  label="Ad Soyad"
                  column="full_name"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Kayit Donemi"
                  column="training_period"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Grup"
                  column="training_group"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Alt Grup"
                  column="training_subgroup"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Iletisim"
                  column="phone"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Ehliyet"
                  column="license_class"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th scope="col">İlerleme</th>
                <SortableHeader
                  label="Teorik"
                  column="theoretical_exam_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Direksiyon"
                  column="driving_exam_date"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <SortableHeader
                  label="Durum"
                  column="status"
                  sortBy={sortBy}
                  sortDir={sortDir}
                  onSort={handleSort}
                />
                <th scope="col" aria-label="Aksiyonlar"></th>
              </tr>
            </thead>
            <tbody>
              {items.map((student) => {
                const wa = buildWhatsAppUrl(student.phone);
                const cohort = student.training_cohort;
                return (
                  <tr key={student.id}>
                    {canManageStatus && (
                      <td className="kp-students__select-col">
                        <input
                          type="checkbox"
                          checked={selectedIds.has(student.id)}
                          aria-label={`${student.full_name} sec`}
                          onChange={() => toggleSelect(student.id)}
                        />
                      </td>
                    )}
                    <td className="kp-students__tc-col kp-cell-nowrap kp-tnum">
                      {student.identity_no || "—"}
                    </td>
                    <td className="kp-students__name-col kp-cell-nowrap">
                      <strong>{student.full_name}</strong>
                    </td>
                    <td className="kp-students__cohort-cell">
                      {cohort?.period_label ?? <span className="kp-students__cohort--empty">—</span>}
                    </td>
                    <td className="kp-students__cohort-cell">
                      {cohort?.group_label ?? <span className="kp-students__cohort--empty">—</span>}
                    </td>
                    <td className="kp-students__cohort-cell">
                      {cohort?.subgroup_label ?? <span className="kp-students__cohort--empty">—</span>}
                    </td>
                    <td className="kp-students__contact-col kp-cell-phone">
                      <div className="kp-students__contact">
                        {student.phone ? formatPhoneDisplay(student.phone) : "—"}
                        {wa && <WhatsAppLink href={wa} />}
                      </div>
                    </td>
                    <td>{student.license_class || "-"}</td>
                    <td className="kp-students__progress-cell">
                      {student.lesson_progress ? (
                        <StudentLessonProgressCell progress={student.lesson_progress} />
                      ) : (
                        "—"
                      )}
                    </td>
                    <CompactExamDateCell
                      date={student.theoretical_exam_date}
                      time={student.theoretical_exam_time}
                    />
                    <CompactExamDateCell
                      date={student.driving_exam_date ?? student.exam_date}
                      time={student.driving_exam_time}
                    />
                    <td className="kp-students__status-col">
                      {canManageStatus ? (
                        <StudentStatusSelect
                          value={student.status}
                          onChange={(status) => handleRowStatus(student.id, status)}
                        />
                      ) : (
                        <span
                          className={`kp-students__status-readonly kp-students__status--${student.status}`}
                        >
                          {STUDENT_STATUS_LABELS[student.status]}
                        </span>
                      )}
                    </td>
                    <td className="kp-students__actions-col">
                      <div className="kp-students__row-actions">
                        <button
                          type="button"
                          className="kp-btn kp-students__action"
                          onClick={() => setLessonStatsFor(student)}
                        >
                          Ders özeti
                        </button>
                        <button
                          type="button"
                          className="kp-btn kp-students__action"
                          onClick={() => setEditing(student)}
                        >
                          Duzenle
                        </button>
                      </div>
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
          </>
        )}
        <ListPaginationBar
          total={total}
          offset={offset}
          limit={pageSize}
          loading={list.loading}
          onOffsetChange={setOffset}
        />
      </div>

      {lessonStatsFor && (
        <StudentLessonStatsDrawer
          student={lessonStatsFor}
          onClose={() => setLessonStatsFor(null)}
        />
      )}

      {(creating || editing) && (
        <StudentFormModal
          student={editing}
          cohortTree={cohortTree.tree}
          onClose={() => {
            setCreating(false);
            setEditing(null);
          }}
          onSaved={() => {
            setCreating(false);
            setEditing(null);
            list.reload();
            cohortTree.reload();
          }}
        />
      )}

      {importing && (
        <StudentImportModal
          onClose={() => setImporting(false)}
          onImported={() => {
            list.reload();
            cohortTree.reload();
          }}
        />
      )}

      {examPdfImporting && (
        <ExamEntryPdfImportModal
          onClose={() => setExamPdfImporting(false)}
          onImported={() => {
            list.reload();
          }}
        />
      )}

      {creatingPeriod && (
        <TrainingPeriodModal
          onClose={() => setCreatingPeriod(false)}
          onCreated={() => cohortTree.reload()}
        />
      )}

      {managingCohorts && (
        <TrainingCohortManageModal
          onClose={() => setManagingCohorts(false)}
          onChanged={() => cohortTree.reload()}
        />
      )}
    </div>
  );
}
